import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { AutoEvaluationLabel } from '../../../../../utils/types/JobOpenings';

interface Box {
  id: number;
  label: AutoEvaluationLabel;
  isEditing?: boolean;
}

interface AddRemoveComponentProps {
  initialLabels: AutoEvaluationLabel[];
  onLabelsChange: (labels: AutoEvaluationLabel[]) => void;
  disabled?: boolean;
  desirableSkills?: string[];
  title?: string;
  showBulkOperators?: boolean;
}

const AddOrRemoveListWithOperators: React.FC<AddRemoveComponentProps> = ({
  initialLabels,
  onLabelsChange,
  disabled,
  desirableSkills,
  title,
  showBulkOperators = true,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const LF = ADMIN_PAGES_KEYS.jobOpenings.listFields;
  const [boxes, setBoxes] = useState<Box[]>(() => {
    return initialLabels.map((label, index) => ({
      id: index,
      label,
      isEditing: false,
    }));
  });
  const [count, setCount] = useState(() => {
    return initialLabels.length;
  });
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const labels = boxes.map((box) => box.label);
    onLabelsChange(labels);
  }, [boxes]);

  const handleToggleOperator = (id: number) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return {
            ...box,
            label: {
              ...box.label,
              relationToNext: box.label.relationToNext === 'AND' ? 'OR' : 'AND',
            },
          };
        }
        return box;
      }),
    );
  };

  const handleAddBox = () => {
    if (inputValue.trim()) {
      const newBox: Box = {
        id: count,
        label: {
          value: inputValue,
          relationToNext: 'OR',
        },
        isEditing: false,
      };
      setBoxes([...boxes, newBox]);
      setInputValue('');
      setCount((prevCount) => prevCount + 1);
    }
    setShowInput(false);
  };

  const handleRemove = (id: number) => {
    setBoxes(boxes.filter((box) => box.id !== id));
  };

  const handleStartEdit = (id: number) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return { ...box, isEditing: true };
        }
        return box;
      }),
    );
  };

  const handleStopEdit = (id: number, newLabel: string) => {
    setBoxes(
      boxes.map((box) => {
        if (box.id === id) {
          return {
            ...box,
            label: {
              value: newLabel,
              relationToNext: box.label.relationToNext,
            },
            isEditing: false,
          };
        }
        return box;
      }),
    );
  };

  const handleClickAdd = () => {
    setShowInput(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleAddDesirableSkills = () => {
    if (desirableSkills && desirableSkills.length > 0) {
      // Get existing skill values to prevent duplicates
      const existingSkills = boxes.map((box) => box.label.value);

      // Filter out skills that already exist
      const newSkills = desirableSkills.filter(
        (skill) => !existingSkills.includes(skill),
      );

      if (newSkills.length > 0) {
        const newBoxes: Box[] = newSkills.map((skill, index) => ({
          id: count + index,
          label: {
            value: skill,
            relationToNext: 'OR',
          },
          isEditing: false,
        }));
        setBoxes((prevBoxes) => [...prevBoxes, ...newBoxes]);
        setCount((prevCount) => prevCount + newSkills.length);
      }
    }
  };

  const handleRemoveDesirableSkills = () => {
    if (desirableSkills && desirableSkills.length > 0) {
      // Remove all boxes that contain desirable skills
      setBoxes((prevBoxes) =>
        prevBoxes.filter((box) => !desirableSkills.includes(box.label.value)),
      );
    }
  };

  const handleBulkToggleOperator = (newOperator: 'AND' | 'OR') => {
    if (disabled) return;
    setBoxes(
      boxes.map((box, index) => {
        if (index === boxes.length - 1) return box; // Skip last box as it doesn't have a relationToNext
        return {
          ...box,
          label: {
            ...box.label,
            relationToNext: newOperator,
          },
        };
      }),
    );
  };

  return (
    <div className='w-full flex flex-col min-h-[200px]'>
      { /* Header with Title and Bulk Operators */ }
      <div className='w-full flex flex-col min-h-[200px]'>
        { /* Header Row: Title */ }
        { title && (
          <div className='mb-2'>
            <h3 className='text-sm font-medium text-gray-900'>{ title }</h3>
          </div>
        ) }

        { /* Content Area: Items or Empty State */ }
        <div className='flex-1'>
          { boxes.length === 0 ? (
            <div className='flex w-full flex-col items-center justify-center py-8 border-gray-200'>
              <div className='w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3'>
                <svg
                    className='w-6 h-6 text-gray-400'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                  />
                </svg>
              </div>
              <p className='text-sm font-medium text-gray-600 mb-1'>
                { t(LF.noItems) }
              </p>
              <p className='text-xs text-gray-500'>
                { t(LF.addNewHint) }
              </p>
            </div>
          ) : (
            <div
                className='flex flex-wrap items-center'
                style={{ gap: '10px' }}
            >
              { boxes.map((box, index) => (
                <React.Fragment key={box.id}>
                  <div
                      className={`relative group flex items-center ${
                        box.isEditing
                          ? 'bg-emerald-50 border-emerald-400 shadow-md ring-2 ring-emerald-100'
                          : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-emerald-300 hover:shadow-sm'
                      } border rounded-md transition-all duration-200 py-2.5 px-3 min-w-[200px] flex-grow md:flex-grow-0`}
                  >
                    { box.isEditing ? (
                      <input
                          type='text'
                          className='w-full min-w-[400px] px-2 py-0.5 bg-transparent outline-none text-sm font-medium text-gray-800'
                          defaultValue={box.label.value}
                          onBlur={(e) => handleStopEdit(box.id, e.target.value)}
                          onKeyDown={(e) =>
                            e.key === 'Enter' &&
                          handleStopEdit(box.id, e.currentTarget.value)
                          }
                          autoFocus
                      />
                    ) : (
                      <div
                          className={`flex-1 ${
                            !disabled
                              ? 'cursor-pointer hover:text-emerald-600'
                              : ''
                          } text-xs font-medium transition-colors duration-200 pr-2`}
                          onClick={() => !disabled && handleStartEdit(box.id)}
                      >
                        { box.label.value }
                      </div>
                    ) }
                    { !disabled && (
                      <button
                          onClick={() => handleRemove(box.id)}
                          className='opacity-0 group-hover:opacity-100 hover:bg-red-50 p-1 rounded transition-all duration-200 flex-shrink-0'
                          aria-label='Remove item'
                      >
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-3 w-3 text-red-500'
                            viewBox='0 0 20 20'
                            fill='currentColor'
                        >
                          <path
                              fillRule='evenodd'
                              d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                              clipRule='evenodd'
                          />
                        </svg>
                      </button>
                    ) }
                  </div>
                  { index < boxes.length - 1 && (
                    <div className='group/operator'>
                      <button
                          onClick={() =>
                            !disabled && handleToggleOperator(box.id)
                          }
                          className={`text-xs font-medium px-3 py-1.5 rounded-md border ${
                            !disabled
                              ? 'cursor-pointer bg-white hover:bg-gray-50 border-gray-300 text-gray-700 hover:text-gray-900 hover:border-emerald-300 hover:ring-1 hover:ring-emerald-200'
                              : 'bg-gray-50 border-gray-200 text-gray-500 cursor-default'
                          } transition-all duration-200 shadow-sm relative`}
                          style={{ minWidth: '45px', textAlign: 'center' }}
                      >
                        { box.label.relationToNext }
                        { !disabled && (
                          <span className='absolute bottom-0 right-0 text-[8px] text-emerald-500 opacity-0 group-hover/operator:opacity-100 transition-opacity duration-200 pr-0.5 pb-0.5'>
                            ↺
                          </span>
                        ) }
                      </button>
                    </div>
                  ) }
                </React.Fragment>
              )) }
            </div>
          ) }
        </div>

        { /* Input/Button Area - Always at bottom */ }
        <div className='flex gap-2 mt-auto pt-4'>
          { showInput ? (
            <div className='flex-1 flex gap-2'>
              <input
                  ref={inputRef}
                  type='text'
                  className='flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200'
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddBox()}
                  onBlur={handleAddBox}
                  placeholder={t(LF.newItemPlaceholder)}
              />
              <button
                  onClick={() => setShowInput(false)}
                  className='px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200'
              >
                { t(LF.cancelInput) }
              </button>
            </div>
          ) : (
            !disabled && (
              <div className='flex gap-2 flex-wrap'>
                <button
                    onClick={handleClickAdd}
                    className='flex items-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 font-medium py-2 px-3 rounded-lg text-sm transition-all duration-200'
                >
                  <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-4 w-4'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                  >
                    <path
                        fillRule='evenodd'
                        d='M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z'
                        clipRule='evenodd'
                    />
                  </svg>
                  { t(LF.addNew) }
                </button>
                { desirableSkills &&
                  desirableSkills.length > 0 &&
                  (() => {
                    const existingSkills = boxes.map((box) => box.label.value);
                    const availableToAdd = desirableSkills.filter(
                      (skill) => !existingSkills.includes(skill),
                    );
                    const alreadyAdded = desirableSkills.filter((skill) =>
                      existingSkills.includes(skill),
                    );

                    return (
                      <>
                        { availableToAdd.length > 0 && (
                          <button
                              onClick={handleAddDesirableSkills}
                              className='flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium py-2 px-3 rounded-lg text-sm transition-all duration-200'
                          >
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-4 w-4'
                                viewBox='0 0 20 20'
                                fill='currentColor'
                            >
                              <path
                                  fillRule='evenodd'
                                  d='M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z'
                                  clipRule='evenodd'
                              />
                            </svg>
                            { t(LF.addDesirable, { count: availableToAdd.length }) }
                          </button>
                        ) }
                        { alreadyAdded.length > 0 && (
                          <button
                              onClick={handleRemoveDesirableSkills}
                              className='flex items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 font-medium py-2 px-3 rounded-lg text-sm transition-all duration-200'
                          >
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-4 w-4'
                                viewBox='0 0 20 20'
                                fill='currentColor'
                            >
                              <path
                                  fillRule='evenodd'
                                  d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                  clipRule='evenodd'
                              />
                            </svg>
                            { t(LF.removeDesirable, { count: alreadyAdded.length }) }
                          </button>
                        ) }
                      </>
                    );
                  })() }
              </div>
            )
          ) }
        </div>
      </div>
    </div>
  );
};

export default AddOrRemoveListWithOperators;
